/**
 * ReadinessFindingCard — one row of the "To fix" or "FYI" list inside
 * the ReadinessPanel. Renders a single ReadinessFinding with:
 *
 *   - status icon (⛔ for blocker, ⚠ for advisory, ⓘ for info)
 *   - user_title (one line)
 *   - user_detail (one or two sentences)
 *   - exactly ONE primary action button (derived from primary_action)
 *   - collapsed "More details" expander with structured evidence
 *
 * Click and hover are reported back via callbacks so the host can
 * wire bi-directional finding ↔ canvas selection without this card
 * knowing about the canvas.
 */
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Block as BlockerIcon,
  WarningAmber as AdvisoryIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

import type {
  ReadinessFinding,
  ReadinessResolutionRequest,
} from '../../../types/readiness';
import ReadinessResolutionForm from './ReadinessResolutionForm';

/**
 * What the server told us about background work scheduled in response
 * to the operator's resolve submit. Hosts derive this from the
 * ``PostMappingResolveResponse`` and hand it back via ``onResolve``'s
 * fulfilled value so the card can switch into an ``applying`` state.
 */
export interface ResolveDispatchInfo {
  /** Map of dispatch channel → status (e.g. ``"scheduled"``). */
  dispatched: Record<string, string>;
  /** Plain-English next-step copy from the server. */
  next_step?: string;
}

interface ReadinessFindingCardProps {
  finding: ReadinessFinding;
  /**
   * Called when the user clicks the primary action. Host decides where
   * the deep-link goes based on ``finding.primary_action`` +
   * ``finding.primary_action_target``.
   */
  onPrimaryAction?: (finding: ReadinessFinding) => void;
  /** Called when the user clicks the card itself (not the button). */
  onCardClick?: (finding: ReadinessFinding) => void;
  /** Called on hover enter / leave for canvas edge emphasis. */
  onHoverEnter?: (finding: ReadinessFinding) => void;
  onHoverLeave?: (finding: ReadinessFinding) => void;
  /** When true, card is rendered with a highlighted border (host selection). */
  highlighted?: boolean;
  /**
   * Optional inline-resolution submitter. When provided AND the finding
   * carries ``resolution_options`` + a non-empty ``finding_id``, the
   * card replaces the navigation-button affordance with an inline
   * ``ReadinessResolutionForm``. Host wires this to either the
   * pre-mapping resolve API (Generate Mapping dialog) or the post-
   * mapping resolve API (workspace inspector / mapping canvas). The
   * promise resolves when the resolution has been applied; the host
   * is responsible for refreshing readiness afterwards.
   *
   * The promise may resolve to a ``ResolveDispatchInfo`` object
   * describing whether the server scheduled async work (per-edge
   * regenerate, scoped materialize). When present, the card switches
   * to an ``applying`` visual state so the operator sees that the
   * platform is acting on their submit rather than the card silently
   * sitting after the toast.
   */
  onResolve?: (
    request: ReadinessResolutionRequest,
  ) => Promise<ResolveDispatchInfo | void>;
  /** Optional lookup-file uploader threaded through to the form. */
  onUploadLookup?: (file: File) => Promise<{ alias: string }>;
}

const ACTION_LABELS: Record<string, string> = {
  fix_in_mapping: 'Fix in mapping',
  review_data: 'Review data',
  adjust_schema: 'Adjust schema',
};

function StatusIcon({
  severity,
}: {
  severity: ReadinessFinding['severity'];
}): React.ReactElement {
  if (severity === 'blocker') {
    return <BlockerIcon fontSize="small" color="error" />;
  }
  if (severity === 'advisory') {
    return <AdvisoryIcon fontSize="small" color="warning" />;
  }
  return <InfoIcon fontSize="small" color="info" />;
}

export function ReadinessFindingCard({
  finding,
  onPrimaryAction,
  onCardClick,
  onHoverEnter,
  onHoverLeave,
  highlighted = false,
  onResolve,
  onUploadLookup,
}: ReadinessFindingCardProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const [resolveSubmitting, setResolveSubmitting] = React.useState(false);
  const [resolveError, setResolveError] = React.useState<string | undefined>();
  // Applying = server scheduled async work in response to the submit.
  // The card surfaces a progress strip + the server's next_step copy
  // so the operator sees "the platform is acting on my change" rather
  // than the card sitting silent after the toast. Cleared when the
  // finding is no longer present in the panel's findings (the host
  // unmounts the card) or after the timeout fallback below.
  const [applying, setApplying] = React.useState<
    { message: string } | null
  >(null);
  // Defensive timeout: in case the bg work never lands (network
  // failure, etc.) clear the applying state after 90s so the operator
  // can retry. The host's polling burst usually catches the change
  // before this fires.
  React.useEffect(() => {
    if (!applying) return;
    const handle = window.setTimeout(() => setApplying(null), 90_000);
    return () => window.clearTimeout(handle);
  }, [applying]);

  const actionLabel = ACTION_LABELS[finding.primary_action];
  // When the host supplies an inline-resolution submitter AND the
  // finding has options, the form REPLACES the navigation button —
  // they're alternative UX paths to the same goal. Keep the nav button
  // available only when no inline-resolution path exists.
  const hasInlineResolution =
    !!onResolve
    && !!finding.finding_id
    && (finding.resolution_options?.length ?? 0) > 0;
  const showAction = !!actionLabel
    && finding.primary_action !== 'none'
    && !hasInlineResolution;

  const handleCardClick = (e: React.MouseEvent) => {
    // Only fire card-click when the user didn't hit the button / expander.
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    onCardClick?.(finding);
  };

  return (
    <Paper
      variant="outlined"
      onClick={handleCardClick}
      onMouseEnter={() => onHoverEnter?.(finding)}
      onMouseLeave={() => onHoverLeave?.(finding)}
      sx={{
        p: 1.5,
        cursor: onCardClick ? 'pointer' : 'default',
        borderColor: highlighted ? 'primary.main' : 'divider',
        borderWidth: highlighted ? 2 : 1,
        transition: 'border-color 120ms ease',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ pt: 0.25 }}>
          <StatusIcon severity={finding.severity} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            {finding.user_title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, lineHeight: 1.4 }}
          >
            {finding.user_detail}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 1 }}
          >
            {showAction && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<ArrowIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onPrimaryAction?.(finding);
                }}
              >
                {actionLabel}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              aria-label={expanded ? 'Hide details' : 'Show details'}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
          {hasInlineResolution && !applying && (
            <ReadinessResolutionForm
              finding={finding}
              submitting={resolveSubmitting}
              errorMessage={resolveError}
              onSubmit={async (req) => {
                setResolveError(undefined);
                setResolveSubmitting(true);
                try {
                  const result = await onResolve!(req);
                  // If the server scheduled background work, switch
                  // the card into the applying state so the operator
                  // sees ongoing progress instead of an inert form.
                  // No dispatch → the action was synchronous and the
                  // host's readiness refresh will remove the card on
                  // its own.
                  const dispatched =
                    result && Object.keys(result.dispatched || {}).length > 0
                      ? result
                      : null;
                  if (dispatched) {
                    setApplying({
                      message:
                        dispatched.next_step ||
                        'Applying your change…',
                    });
                  }
                } catch (err: unknown) {
                  // Surface the failure inline (Alert above the form).
                  // Don't rethrow — the host already saw the error via
                  // its toast/handler, and React's runtime overlay
                  // would otherwise unmount the panel on every
                  // validation rejection.
                  setResolveError(
                    err instanceof Error
                      ? err.message
                      : 'Could not apply your choice — try again.',
                  );
                } finally {
                  setResolveSubmitting(false);
                }
              }}
              onUploadLookup={onUploadLookup}
            />
          )}
          {applying && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.75 }}
              >
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">
                  {applying.message}
                </Typography>
              </Stack>
              <LinearProgress sx={{ height: 3, borderRadius: 1 }} />
            </Box>
          )}
          <Collapse in={expanded} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              <DetailRow label="Where" value={finding.target_path} />
              {finding.parent_identity &&
                Object.keys(finding.parent_identity).length > 0 && (
                  <DetailRow
                    label="Parent"
                    value={JSON.stringify(finding.parent_identity)}
                  />
                )}
              {Object.keys(finding.observed).length > 0 && (
                <DetailRow
                  label="Observed"
                  value={JSON.stringify(finding.observed)}
                />
              )}
              {Object.keys(finding.expected).length > 0 && (
                <DetailRow
                  label="Expected"
                  value={JSON.stringify(finding.expected)}
                />
              )}
            </Box>
          </Collapse>
        </Box>
      </Stack>
    </Paper>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.25 }}>
      <Typography
        component="span"
        sx={{ minWidth: 70, color: 'text.secondary', fontSize: 'inherit' }}
      >
        {label}:
      </Typography>
      <Typography
        component="span"
        sx={{ wordBreak: 'break-all', fontSize: 'inherit' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default ReadinessFindingCard;
